/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */

import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl-next';

// eslint-disable-next-line no-unused-vars
import { cssMap, cx, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import CrossIcon from '@atlaskit/icon/core/cross';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import Image from '@atlaskit/image';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import VisuallyHidden from '@atlaskit/visually-hidden';

import PreviewBg from '../../assets/preview-bg.svg';

import messages from './messages';

const styles = cssMap({
	previewCard: {
		maxWidth: '204px',
		height: '64px',
		position: 'relative',
		borderRadius: token('radius.large'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		paddingInlineStart: token('space.150'),
		backgroundColor: token('elevation.surface.raised'),

		'&:hover': {
			backgroundColor: token('elevation.surface.raised.hovered'),
		},
	},
	previewCardContent: {
		minWidth: '0px', // Required for text truncation
	},
	iconWrapper: {
		overflow: 'hidden',
		backgroundColor: token('color.background.neutral'),
		height: '48px',
		width: '48px',
		borderRadius: token('radius.small'),
		position: 'relative',
		flexShrink: 0,
	},
	icon: {
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
	},
	crossIcon: {
		position: 'absolute',
		right: token('space.negative.150'),
		top: token('space.negative.150'),
		borderRadius: token('radius.full'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderColor: token('color.border'),
		backgroundColor: token('elevation.surface'),
		height: '24px',
		width: '24px',
		opacity: 0,

		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
	},
	crossIconVisible: {
		opacity: 1,
	},
	// Todo: check why Text maxLines doesn't work
	labelWrapper: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	thumbnailImageCover: {
		width: '100%',
		height: '100%',
		objectFit: 'cover',
	},
	thumbnailImageContain: {
		width: '100%',
		height: '100%',
		objectFit: 'contain',
	},
	clickable: {
		cursor: 'pointer',
		outline: 'none',
		'&:focus-visible': {
			outline: `${token('border.width.focused')} solid ${token('color.border.focused')}`,
			outlineOffset: token('space.025'),
		},
	},
});

const LoadingIndicator = () => {
	return <Spinner size="small" />;
};

type MediaContentRendererProps = {
	isLoading?: boolean;
	error?: string;
	imageSrc?: string;
	cardIcon?: React.ReactNode;
	errorLabel?: string;
};

const MediaContentRenderer = ({
	isLoading,
	error,
	imageSrc,
	cardIcon,
	errorLabel,
}: MediaContentRendererProps) => {
	if (isLoading) {
		return <LoadingIndicator />;
	}

	if (error) {
		return <StatusErrorIcon label={errorLabel || error} color={token('color.icon.danger')} />;
	}

	if (imageSrc) {
		return (
			<Box
				as="img"
				src={imageSrc}
				alt=""
				// Suppress exposure: This check fires when PreviewCard renders for styling purposes.
				xcss={
					FeatureGates.getExperimentValue(
						'rovo_chat_file_previews_experiment',
						'isEnabled',
						false,
						{
							fireExperimentExposure: false,
						},
					)
						? styles.thumbnailImageCover
						: styles.thumbnailImageContain
				}
			/>
		);
	}

	if (cardIcon) {
		return cardIcon;
	}

	return <Image src={PreviewBg} alt="" />;
};

type MediaWrapperProps = MediaContentRendererProps & {
	show?: boolean;
};

const MediaWrapper = ({ show = true, ...contentProps }: MediaWrapperProps) => {
	if (!show) {
		return null;
	}

	const shouldShowCentered = contentProps.isLoading || contentProps.error;

	return (
		<Box xcss={styles.iconWrapper}>
			<Suspense fallback={<LoadingIndicator />}>
				{contentProps.imageSrc && !shouldShowCentered ? (
					<MediaContentRenderer {...contentProps} />
				) : (
					<>
						{!contentProps.isLoading && <Image src={PreviewBg} alt="" />}
						<Box xcss={styles.icon}>
							<MediaContentRenderer {...contentProps} />
						</Box>
					</>
				)}
			</Suspense>
		</Box>
	);
};

type RemoveButtonProps = {
	onRemove?: () => void;
	isVisible?: boolean;
	removeLabel?: string;
};

const RemoveButton = ({ onRemove, isVisible = false, removeLabel }: RemoveButtonProps) => {
	if (!onRemove) {
		return null;
	}

	return (
		<Pressable
			onClick={onRemove}
			xcss={cx(styles.crossIcon, isVisible && styles.crossIconVisible)}
			// @ts-ignore - data attributes not in Pressable types
			data-remove-button="true"
		>
			<CrossIcon
				label={removeLabel || ''}
				spacing="compact"
				size="small"
				color={token('color.icon')}
			/>
		</Pressable>
	);
};

const useAutoScroll = (autoScroll?: boolean) => {
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (autoScroll && ref.current) {
			ref.current?.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
				inline: 'nearest',
			});
		}
	}, [autoScroll]);

	return ref;
};

type LabelContentProps = {
	label: string;
	subLabel?: string;
	subLabelIcon?: React.ReactNode;
};

const LabelContent = ({ label, subLabel, subLabelIcon }: LabelContentProps) => (
	<Stack xcss={styles.previewCardContent} space="space.050">
		<Box xcss={styles.labelWrapper}>
			<Text color="color.text" size="medium">
				{label}
			</Text>
		</Box>

		{subLabel && (
			<Inline space="space.050" alignBlock="center">
				{subLabelIcon && <Suspense fallback={null}>{subLabelIcon}</Suspense>}
				<Text color="color.text.subtlest" size="small">
					{subLabel}
				</Text>
			</Inline>
		)}
	</Stack>
);

type PreviewCardProps = {
	isLoading?: boolean;
	error?: string;
	onRemove?: () => void;
	onClick?: () => void;
	autoScroll?: boolean;
	label: string;
	subLabel?: string;
	subLabelIcon?: React.ReactNode;
	cardIcon?: React.ReactNode;
	imageSrc?: string;
	removeLabel?: string;
	errorLabel?: string;
	testId?: string;
};

export const PreviewCard = ({
	onRemove,
	onClick,
	isLoading,
	error,
	autoScroll,
	label,
	subLabel,
	subLabelIcon,
	cardIcon,
	imageSrc,
	removeLabel,
	errorLabel,
	testId,
}: PreviewCardProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const [isHovered, setIsHovered] = useState(false);
	const ref = useAutoScroll(autoScroll);

	const showMedia = !!(cardIcon || imageSrc);

	const handleClick = useCallback(
		(e: React.MouseEvent) => {
			// Don't trigger onClick if clicking the remove button
			if ((e.target as HTMLElement).closest('[data-remove-button]')) {
				return;
			}
			onClick?.();
		},
		[onClick],
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			// Handle Enter and Space keys for accessibility
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				onClick?.();
			}
		},
		[onClick],
	);

	return (
		<Tooltip content={error || label} position="top">
			<Box
				ref={ref}
				testId={testId}
				tabIndex={onClick ? 0 : undefined}
				role={onClick ? 'button' : undefined}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				onFocus={() => setIsHovered(true)}
				onBlur={() => setIsHovered(false)}
				onClick={onClick ? handleClick : undefined}
				onKeyDown={onClick ? handleKeyDown : undefined}
				xcss={onClick ? styles.clickable : undefined}
			>
				<Inline
					xcss={styles.previewCard}
					alignBlock="center"
					space="space.150"
					spread="space-between"
				>
					<LabelContent label={label} subLabel={subLabel} subLabelIcon={subLabelIcon} />

					<MediaWrapper
						show={showMedia}
						isLoading={isLoading}
						error={error}
						imageSrc={imageSrc}
						cardIcon={cardIcon}
						errorLabel={errorLabel}
					/>

					<RemoveButton
						onRemove={onRemove}
						isVisible={isHovered && !!onRemove}
						removeLabel={removeLabel}
					/>
				</Inline>
				{isLoading ? (
					<div role="status">
						<VisuallyHidden>{formatMessage(messages.loadingLabel, { label })}</VisuallyHidden>
					</div>
				) : null}
			</Box>
		</Tooltip>
	);
};
